import React from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

const SugarQuotaAnalysis = () => {
  const currentQuotaStatus = {
    totalAllocated: 50000,
    utilized: 38500,
    remaining: 11500,
    utilizationPercentage: 77,
    month: 'December 2025',
    quarter: 'Q4 2025'
  };

  const quotaBreakdown = [
    {
      category: 'Levy Sugar',
      allocated: 15000,
      utilized: 12500,
      remaining: 2500,
      deadline: '2025-12-31',
      status: 'On Track'
    },
    {
      category: 'Free Sale Sugar',
      allocated: 25000,
      utilized: 19000,
      remaining: 6000,
      deadline: '2025-12-31',
      status: 'On Track'
    },
    {
      category: 'Export Quota',
      allocated: 10000,
      utilized: 7000,
      remaining: 3000,
      deadline: '2025-12-31',
      status: 'Below Target'
    }
  ];

  const complianceAlerts = [
    {
      type: 'Submission Due',
      category: 'Monthly Report',
      dueDate: '2025-12-05',
      priority: 'High',
      message: 'November production and sale report due in 4 days'
    },
    {
      type: 'Documentation',
      category: 'Export Documents',
      dueDate: '2025-12-08',
      priority: 'Medium',
      message: 'Export compliance documentation required for 3000 MT shipment'
    },
    {
      type: 'Utilization Alert',
      category: 'Export Quota',
      dueDate: '2025-12-31',
      priority: 'Medium',
      message: 'Export quota utilization at 70% - consider expediting exports'
    }
  ];

  const historicalTrends = [
    { month: 'Jul 2025', allocated: 45000, utilized: 42000, percentage: 93.3 },
    { month: 'Aug 2025', allocated: 48000, utilized: 44500, percentage: 92.7 },
    { month: 'Sep 2025', allocated: 47000, utilized: 43500, percentage: 92.6 },
    { month: 'Oct 2025', allocated: 49000, utilized: 46000, percentage: 93.9 },
    { month: 'Nov 2025', allocated: 51000, utilized: 47500, percentage: 93.1 },
    { month: 'Dec 2025', allocated: 50000, utilized: 38500, percentage: 77.0 }
  ];

  const forecastAnalysis = [
    {
      period: 'Jan 2026',
      predictedDemand: 52000,
      recommendedQuota: 54000,
      basis: 'Historical trend + 5% growth',
      confidence: 'High'
    },
    {
      period: 'Feb 2026',
      predictedDemand: 48000,
      recommendedQuota: 50000,
      basis: 'Seasonal adjustment - post-festival dip',
      confidence: 'Medium'
    },
    {
      period: 'Q1 2026',
      predictedDemand: 155000,
      recommendedQuota: 160000,
      basis: 'Quarterly projection with buffer',
      confidence: 'High'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'On Track': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'Below Target': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      'Critical': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      'Medium': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      'Low': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  };

  const getConfidenceColor = (confidence) => {
    const colors = {
      'High': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      'Medium': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      'Low': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors?.[confidence] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <BreadcrumbNavigation />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Sugar Quota Analysis</h1>
            <p className="text-muted-foreground">Government quota allocations, utilization tracking, and compliance monitoring</p>
          </div>

          {/* Current Quota Status Overview */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">{currentQuotaStatus?.month}</h2>
                <p className="text-sm text-muted-foreground">{currentQuotaStatus?.quarter}</p>
              </div>
              <Select
                options={[
                  { value: 'current', label: 'Current Month' },
                  { value: 'quarter', label: 'Current Quarter' },
                  { value: 'year', label: 'Current Year' }
                ]}
                value="current"
                className="w-48"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Target" className="text-primary" size={24} />
                </div>
                <h3 className="text-2xl font-bold text-foreground">{currentQuotaStatus?.totalAllocated?.toLocaleString('en-IN')} MT</h3>
                <p className="text-sm text-muted-foreground">Total Allocated</p>
              </div>

              <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="CheckCircle" className="text-green-600 dark:text-green-400" size={24} />
                </div>
                <h3 className="text-2xl font-bold text-foreground">{currentQuotaStatus?.utilized?.toLocaleString('en-IN')} MT</h3>
                <p className="text-sm text-muted-foreground">Utilized</p>
              </div>

              <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Package" className="text-orange-600 dark:text-orange-400" size={24} />
                </div>
                <h3 className="text-2xl font-bold text-foreground">{currentQuotaStatus?.remaining?.toLocaleString('en-IN')} MT</h3>
                <p className="text-sm text-muted-foreground">Remaining</p>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="TrendingUp" className="text-blue-600 dark:text-blue-400" size={24} />
                </div>
                <h3 className="text-2xl font-bold text-foreground">{currentQuotaStatus?.utilizationPercentage}%</h3>
                <p className="text-sm text-muted-foreground">Utilization Rate</p>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Overall Quota Utilization</span>
                <span className="text-sm font-medium text-foreground">{currentQuotaStatus?.utilizationPercentage}%</span>
              </div>
              <div className="bg-muted rounded-full h-3">
                <div
                  className="bg-primary h-3 rounded-full transition-all duration-500"
                  style={{ width: `${currentQuotaStatus?.utilizationPercentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Quota Breakdown by Category */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-6">Quota Allocation by Category</h2>
            
            <div className="space-y-4">
              {quotaBreakdown?.map((quota, index) => (
                <div key={index} className="p-4 border border-border rounded-lg">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">{quota?.category}</h3>
                      <p className="text-sm text-muted-foreground">Deadline: {new Date(quota?.deadline)?.toLocaleDateString('en-IN')}</p>
                    </div>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(quota?.status)}`}>
                      {quota?.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Allocated</p>
                      <p className="text-sm font-medium text-foreground">{quota?.allocated?.toLocaleString('en-IN')} MT</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Utilized</p>
                      <p className="text-sm font-medium text-foreground">{quota?.utilized?.toLocaleString('en-IN')} MT</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                      <p className="text-sm font-medium text-foreground">{quota?.remaining?.toLocaleString('en-IN')} MT</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          ((quota?.utilized / quota?.allocated) * 100) >= 90 ? 'bg-green-500' :
                          ((quota?.utilized / quota?.allocated) * 100) >= 70 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${(quota?.utilized / quota?.allocated) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {((quota?.utilized / quota?.allocated) * 100)?.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Compliance Dashboard */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-6">Compliance Dashboard</h2>
            
            <div className="space-y-4">
              {complianceAlerts?.map((alert, index) => (
                <div key={index} className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Icon name="AlertCircle" className="text-primary" size={20} />
                      <div>
                        <h3 className="font-medium text-foreground">{alert?.type}</h3>
                        <p className="text-sm text-muted-foreground">{alert?.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPriorityColor(alert?.priority)}`}>
                        {alert?.priority}
                      </span>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Due Date</p>
                        <p className="text-sm font-medium text-foreground">{new Date(alert?.dueDate)?.toLocaleDateString('en-IN')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-foreground mb-3">{alert?.message}</p>
                  
                  <Button variant="default" size="sm" iconName="FileText" iconPosition="left">
                    Submit Compliance
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Historical Trends */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">Historical Utilization Trends</h2>
              
              <div className="space-y-4">
                {historicalTrends?.map((trend, index) => (
                  <div key={index} className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">{trend?.month}</span>
                      <span className="text-sm text-muted-foreground">
                        {trend?.utilized?.toLocaleString('en-IN')} / {trend?.allocated?.toLocaleString('en-IN')} MT
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            trend?.percentage >= 90 ? 'bg-green-500' :
                            trend?.percentage >= 75 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${trend?.percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-foreground">{trend?.percentage?.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <Button variant="outline" size="sm" iconName="Download" iconPosition="left" className="w-full">
                  Export Historical Data
                </Button>
              </div>
            </div>

            {/* Forecast Planning */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">Forecast & Planning</h2>
              
              <div className="space-y-4">
                {forecastAnalysis?.map((forecast, index) => (
                  <div key={index} className="p-4 border border-border rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-foreground">{forecast?.period}</h3>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getConfidenceColor(forecast?.confidence)}`}>
                        {forecast?.confidence} Confidence
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Predicted Demand:</span>
                        <span className="font-medium text-foreground">{forecast?.predictedDemand?.toLocaleString('en-IN')} MT</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Recommended Quota:</span>
                        <span className="font-medium text-primary">{forecast?.recommendedQuota?.toLocaleString('en-IN')} MT</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{forecast?.basis}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <Button variant="default" size="sm" iconName="TrendingUp" iconPosition="left" className="w-full">
                  Generate Detailed Forecast Report
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SugarQuotaAnalysis;